import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import LoginForm from "./LoginForm";
import RegisterForm from "./RegisterForm";
import { Button } from "../ui/button";
import { X } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import AuthWrapper from "./AuthWrapper";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: "login" | "register";
}

const AuthModal = ({ isOpen, onClose, initialView = "login" }: AuthModalProps) => {
  const [view, setView] = useState<"login" | "register">(initialView);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setView(initialView);
    }
  }, [isOpen, initialView]);

  // Fallback form when AuthContext is not available
  const handleFallbackSubmit = () => {
    toast({
      title: "Authentication service unavailable",
      description: "Please try again later",
      variant: "destructive",
    });
  };

  const LoginFormFallback = () => (
    <div className="space-y-4">
      <h2 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">
        Welcome Back
      </h2>
      <p className="text-red-500">Authentication service is currently unavailable. Please try again later.</p>
      <Button
        className="w-full bg-primary font-medium text-white hover:bg-red-700"
        onClick={handleFallbackSubmit}
      >
        Login
      </Button>
    </div>
  );

  const RegisterFormFallback = () => (
    <div className="space-y-4">
      <h2 className="mb-6 text-2xl font-bold text-gray-800 dark:text-white">
        Create an Account
      </h2>
      <p className="text-red-500">Registration service is currently unavailable. Please try again later.</p>
      <Button
        className="w-full bg-primary font-medium text-white hover:bg-red-700"
        onClick={handleFallbackSubmit}
      >
        Register
      </Button>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogTitle className="sr-only">
          {view === "login" ? "Log in to your account" : "Create a new account"}
        </DialogTitle>
        <DialogContent className="max-w-md overflow-hidden rounded-xl p-0">
        <div className="flex">
          <button
            onClick={() => setView("login")}
            className={`flex-1 border-b-2 py-4 text-center font-medium ${
              view === "login"
                ? "border-primary text-primary dark:text-primary-dark"
                : "border-transparent text-gray-600 dark:text-gray-400"
            }`}
          >
            Login
          </button>
          <button
            onClick={() => setView("register")}
            className={`flex-1 border-b-2 py-4 text-center font-medium ${
              view === "register"
                ? "border-primary text-primary dark:text-primary-dark"
                : "border-transparent text-gray-600 dark:text-gray-400"
            }`}
          >
            Register
          </button>
        </div>

        <div className="p-4">
          {view === "login" ? (
            <AuthWrapper
              fallback={<LoginFormFallback />}
            >
              <LoginForm onSuccess={onClose} />
            </AuthWrapper>
          ) : (
            <AuthWrapper
              fallback={<RegisterFormFallback />}
            >
              <RegisterForm onSuccess={onClose} />
            </AuthWrapper>
          )}
        </div>

        {/*<div className="border-t border-gray-200 bg-gray-50 p-6 text-center dark:border-gray-700 dark:bg-gray-900">*/}
        {/*  <Button*/}
        {/*    variant="ghost"*/}
        {/*    onClick={onClose}*/}
        {/*    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"*/}
        {/*  >*/}
        {/*    <X className="mr-2 h-4 w-4" />*/}
        {/*    Closek*/}
        {/*  </Button>*/}
        {/*</div>*/}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
